import {
  BackendPaginatedResponse,
  BackendUser,
  Content,
  ReportActivity,
  ReportActivityLog,
  ReportLog,
  ReportedContent,
  ReportedContentAction
} from 'common/types'
import { useUserApi } from 'hooks/use-user-api/useUserApi'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { useContent } from 'pages/content/hooks/useContent'
import { useState } from 'react'
import { useJobs } from 'hooks/use-jobs/useJobs'
import { observer } from 'mobx-react'
import { ReportedContentState } from 'models/reported-content/reported-content.store'
import { useContentApi } from 'hooks/use-content-api/useContentApi'

export const useAdminReportedList = () => {
  const {
    reportedContents,
    setReportedContents,
    reportedContent,
    setReportedContent,
    isLoading,
    setIsLoading,
    reportsById,
    setReportsById,
    totalCount,
    setTotalCount,
    page,
    setPage,
    setIsReady,
    reportActivityPage,
    setReportActivityPage,
    reportActivityLogs, 
    setReportActivityLogs,
    reportLogs, 
    setReportLogs,
    setIsReportLogReady,
    setIsReportActivityLogReady
  } = ReportedContentState
  const reportedListHeadline: string[] = ['Content Owner', 'Type', 'Status', 'Assigned To', 'ContentRef', 'Actions']
  const reportedActionItems: ReportedContentAction[] = [
    ReportedContentAction.VIEW_REPORT,
    ReportedContentAction.CLOSE_REPORT
  ]
  const { backendUser } = useAuth()
  const {
    getReport,
    getContentById,
    fetchAllReports,
    deleteReportedContent,
    fetchReportsByContentId,
    fetchReportActivityLogs
  } = useContent()
  const { fetchUser } = useUserApi()
  const { fetchReportLogs } = useContentApi()
  const { getJobById } = useJobs()
  const { reportedContentActions } = useContentApi()

  const fetchReportedContents = async (
    pageSize?: number,
    searchCriteria?: string,
    status?: string,
    type?: string,
    initialList?: ReportedContent[]
  ) => {
    try {
      const response = await fetchAllReports(backendUser?.id, page, pageSize, searchCriteria, status, type)
      setTotalCount(parseInt(response.totalCount))
      const reports = [...(page < 2 ? initialList : undefined ?? reportedContents), ...response?.data]
      setReportedContents(reports)
      if (response.page.toString() === '1') {
        setIsReady(true)
      }
    } catch (error) {}
  }

  const getReportDetails = async (reportId: string) => {
    try {
      let reporteeUser
      let reporterUser
      let reportedContent
      const report = await getReport(reportId)

      if (report?.type === 'job') {
        ;[reporteeUser, reporterUser, reportedContent] = await Promise.all([
          fetchUser(report.reporteeId),
          fetchUser(report.reporterId),
          getJobById(report?.jobId)
        ])
      } else {
        ;[reporteeUser, reporterUser, reportedContent] = await Promise.all([
          fetchUser(report.reporteeId),
          fetchUser(report.reporterId),
          getContentById(report?.contentId)
        ])
      }

      setReportedContent({
        ...report,
        contentOwnerFirstName: reporteeUser.firstName,
        contentOwnerLastName: reporteeUser.lastName,
        contentReporterFirstName: reporterUser.firstName,
        contentReporterLastName: reporterUser.lastName,
        content: reportedContent,
        title: reportedContent?.title,
        reporteeUser
      })
    } catch (error) {
      return error
    } finally {
      setIsLoading(false)
    }
  }

  const getReportsById = async (contentId: string) => {
    try {
      await fetchReportsByContentId(contentId).then(async response => {
        if (response?.length !== 0) {
          const reports = await Promise.all(
            response?.map(async report => {
              const users = await fetchUser(report?.reporterId)
              return {
                ...report,
                reporter: users?.firstName + ' ' + users?.lastName
              }
            })
          )
          setReportsById(reports)
        }
      })
    } catch (error) {
      return error
    }
  }
  const reportedContentAction = async (userId: string, payload: ReportActivity) => {
    const response: ReportedContent = await reportedContentActions(userId, payload)
    return response
  }
  const getReportActivityLogs = async (reportActivityPage, pageSize: number, contentId: string) => {
    try {
      const reportActivity: BackendPaginatedResponse<ReportActivityLog[]> = await fetchReportActivityLogs(
        reportActivityPage,
        pageSize,
        contentId
      )
      setReportActivityLogs(reportActivity)
      if (reportActivity.page.toString() === '1') {
        setIsReportActivityLogReady(true)
      }
    } catch (error) {
      return error
    }
  }

  const getReportLogs = async (page: number, pageSize: number, contentId: string) => {
    try {
      setIsLoading(true)
      const reportLog: BackendPaginatedResponse<ReportLog[]> = await fetchReportLogs(page, pageSize, contentId)
      setReportLogs(reportLog)
      if (reportLog.page.toString() === '1') {
        setIsReportLogReady(true)
      }
    } catch (error) {
      return error
    } finally {
      setIsLoading(false)
    }
  }

  return {
    reportedListHeadline,
    reportedActionItems,
    getReportDetails,
    deleteReportedContent,
    fetchReportedContents,
    getReportsById,
    reportedContentAction,
    getReportActivityLogs,
    getReportLogs,
  } as const
}
